import { useState } from 'react'
import Swal from 'sweetalert2'
import { useSearchParams } from 'react-router-dom'

import useAxiosPrivate from '../hooks/useAxiosPrivate'

const initialStandardFilter = { pageSize: 50, currentPage: 1, orderBy: '', recordId: '' }

const useCommon = (controllerName, initialFilter) => {
	const [isUpdate, setIsUpdate] = useState(false)
	const [isShowModal, setIsShowModal] = useState(false)
	const [loadingRecord, setLoadingRecord] = useState(false)
	const [loadingTableList, setLoadingTableList] = useState(true)
	const [loadingSave, setLoadingSave] = useState(false)
	const [tableList, setTableList] = useState()
	const [tablePaging, setTablePaging] = useState()
	const [dataInFilter, setDataInFilter] = useState()
	const [dataInControl, setDataInControl] = useState()
	const [filter, setFilter] = useState({ ...initialStandardFilter, ...initialFilter })
	const [selectedItem, setSelectedItem] = useState()

	const [searchParams, setSearchParams] = useSearchParams()

	const axiosPrivate = useAxiosPrivate()

	const handleChangeFilter = (key, value) => {
		setFilter(prev => ({
			...prev,
			[key]: value
		}))
	}

	const handleResetFilter = () => {
		searchParams.set('id', '')
		setSearchParams(searchParams)

		setFilter({
			...initialStandardFilter,
			...initialFilter,
			orderBy: filter.orderBy,
			pageSize: filter.pageSize
		})

		fetchDataList({
			...initialStandardFilter,
			...initialFilter,
			orderBy: filter.orderBy,
			pageSize: filter.pageSize
		})
	}

	const fetchDataList = async filterData => {
		setLoadingTableList(true)

		try {
			const body = filterData ? { ...filter, ...filterData } : filter
			const { data } = await axiosPrivate.post(`/${controllerName}/DataList`, body)

			if (data.dataListTanker) {
				setTableList(data.dataListTanker)
			} else {
				setTableList(data.data?.table_list)
			}

			setTablePaging(data.data?.table_paging[0])
		} catch (err) {
			Swal.fire({
				title: 'Error!',
				text: err.response?.data?.message ?? err.response?.data?.title,
				icon: 'error',
				confirmButtonText: 'Ok'
			})
		} finally {
			setLoadingTableList(false)
		}
	}

	const fetchSearchResultList = async filterData => {
		setLoadingTableList(true)

		try {
			const body = filterData ? { ...filter, ...filterData } : filter
			const { data } = await axiosPrivate.post(
				`/${controllerName}/SearchResultList`,
				body
			)

			setTableList(data.data.table_list)
			setTablePaging(data.data.table_paging[0])
		} catch (err) {
			Swal.fire({
				title: 'Error!',
				text: err.response?.data?.message ?? err.response?.data?.title,
				icon: 'error',
				confirmButtonText: 'Ok'
			})
		} finally {
			setLoadingTableList(false)
		}
	}

	const fetchDataInFilter = async () => {
		const { data } = await axiosPrivate.get(`/${controllerName}/DataInFilters`)
		setDataInFilter(data)
	}

	const fetchDataInControl = async mid => {
		const { data } = await axiosPrivate.get(
			`/${controllerName}/DataInControl?mid=${mid}`
		)

		setDataInControl(data)
	}

	const fetchDataRecord = async id => {
		setLoadingRecord(true)

		try {
			const { data } = await axiosPrivate.get(`/${controllerName}/DataRecord?id=${id}`)

			setSelectedItem(data.data?.table[0])
			setIsUpdate(true)
			setIsShowModal(true)
		} catch (err) {
			Swal.fire({
				title: 'Error!',
				text: err.response?.data?.message ?? err.response?.data?.title,
				icon: 'error',
				confirmButtonText: 'Ok'
			})
		} finally {
			setLoadingRecord(false)
		}
	}

	const handleOrder = name => {
		if (filter.orderBy === name) {
			handleChangeFilter('orderBy', `${name} desc`)
			fetchDataList({ orderBy: `${name} desc` })
		} else {
			handleChangeFilter('orderBy', name)
			fetchDataList({ orderBy: name })
		}
	}

	const deleteRecord = async (mid, itemsToDelete) => {
		Swal.fire({
			title: 'Are you sure?',
			text: 'you want to delete',
			icon: 'warning',
			showCancelButton: true,
			cancelButtonColor: '#bbb',
			confirmButtonText: 'Yes',
			cancelButtonText: 'No'
		}).then(async result => {
			if (result.isConfirmed) {
				setLoadingRecord(true)

				try {
					const { data } = await axiosPrivate.delete(
						`/${controllerName}/DataDelete?mid=${mid}`,
						{
							data: {
								id: itemsToDelete
							}
						}
					)

					Swal.fire({
						title: 'Success',
						text: data.message,
						icon: 'success',
						confirmButtonText: 'Ok'
					})

					await fetchDataList()
				} catch (err) {
					console.log(err)
					Swal.fire({
						title: 'Error!',
						text: err.response?.data?.message ?? err.response?.data?.title,
						icon: 'error',
						confirmButtonText: 'Ok'
					})
				} finally {
					setLoadingRecord(false)
				}
			}
		})
	}

	const handleCloseModal = () => {
		setIsUpdate(false)
		setIsShowModal(false)
		setSelectedItem()
	}

	const saveDraft = async (mid, orderName, bodyData, saveOnly = true) => {
		setLoadingSave(true)

		try {
			const { data } = await axiosPrivate.post(
				`/${controllerName}/CreateAndUpdate?mid=${mid}`,
				bodyData
			)

			handleChangeFilter('orderBy', !isUpdate ? `${orderName} desc` : '')

			if (saveOnly) {
				Swal.fire({
					title: 'Success',
					text: data.message,
					icon: 'success',
					confirmButtonText: 'Ok'
				})

				setLoadingSave(false)
				handleCloseModal()
				fetchDataList({ orderBy: !isUpdate ? `${orderName} desc` : '' })
			}

			return data.data.table[0][orderName]
		} catch (err) {
			Swal.fire({
				title: 'Error!',
				text: err.response?.data?.message ?? err.response?.data?.title,
				icon: 'error',
				confirmButtonText: 'Ok'
			})

			setLoadingSave(false)
		}
	}

	const publishItem = async recordId => {
		try {
			await axiosPrivate.post(`/${controllerName}/PublicRecord?recordid=${recordId}`)

			handleCloseModal()
			fetchDataList()

			Swal.fire({
				title: 'Success',
				icon: 'success',
				confirmButtonText: 'Ok'
			})
		} catch (err) {
			Swal.fire({
				title: 'Error!',
				text: err.response?.data?.message ?? err.response?.data?.title,
				icon: 'error',
				confirmButtonText: 'Ok'
			})
		} finally {
			setLoadingSave(false)
		}
	}

	const copyItem = async (keyName, id) => {
		setLoadingRecord(true)

		try {
			const { data } = await axiosPrivate.get(`/${controllerName}/DataRecord?id=${id}`)

			setSelectedItem({
				...data.data?.table[0],
				[keyName]: 0
			})
			setIsUpdate(true)
			setIsShowModal(true)
		} catch (err) {
			Swal.fire({
				title: 'Error!',
				text: err.response?.data?.message ?? err.response?.data?.title,
				icon: 'error',
				confirmButtonText: 'Ok'
			})
		} finally {
			setLoadingRecord(false)
		}
	}

	const exportExcel = async mid => {
		try {
			setLoadingRecord(true)

			const { data } = await axiosPrivate.post(
				`/${controllerName}/ExportExcel?mid=${mid}`,
				filter
			)

			// console.log(data)

			if (data.data) {
				const link = document.createElement('a')
				link.href = `${process.env.REACT_APP_BASE_ASSETS}/${data.data}`
				link.setAttribute('download', `export.xlsx`)
				document.body.appendChild(link)
				link.click()

				setTimeout(() => {
					link.parentNode.removeChild(link)
					setLoadingRecord(false)
				}, 1000)
			}
		} catch (err) {
			Swal.fire({
				title: 'Error!',
				text: err.response?.data?.message ?? err.response?.data?.title,
				icon: 'error',
				confirmButtonText: 'Ok'
			})
		} finally {
			setLoadingRecord(false)
		}
	}

	return {
		isUpdate,
		setIsUpdate,
		isShowModal,
		setIsShowModal,
		loadingRecord,
		setLoadingRecord,
		loadingTableList,
		setLoadingTableList,
		tableList,
		setTableList,
		tablePaging,
		setTablePaging,
		dataInFilter,
		setDataInFilter,
		dataInControl,
		setDataInControl,
		filter,
		setFilter,
		selectedItem,
		setSelectedItem,
		handleChangeFilter,
		handleResetFilter,
		fetchDataList,
		fetchDataInFilter,
		fetchDataInControl,
		fetchDataRecord,
		handleOrder,
		deleteRecord,
		handleCloseModal,
		saveDraft,
		loadingSave,
		publishItem,
		copyItem,
		exportExcel,
		fetchSearchResultList
	}
}

export default useCommon
